import { Controller } from "stimulus";

/**
 * FormSubmitController
 * ====================
 * Generic AJAX form submission controller with loading states and feedback.
 *
 * WHAT IT DOES:
 * - Submits a form via AJAX (fetch) instead of a full page reload
 * - Shows a loading spinner on the submit button while waiting for response
 * - Displays success/error feedback messages after submission
 * - Hides the form and shows a thank-you message on success
 *
 * BEHAVIOR:
 * - When user clicks submit: Prevents default form submission, dispatches a
 *   "form-submit:before" event (which other controllers can listen to and cancel),
 *   then sends the form data via POST to the form's action URL.
 * - While waiting: The submit button is disabled, button text is hidden, and a
 *   loader element is shown.
 * - On success: The form fades out, is hidden, and a feedback panel fades in
 *   showing the success title and message. The form is also reset.
 * - On error: The loader is hidden, button is re-enabled, and an error message
 *   is displayed.
 *
 * HTML USAGE:
 *   <div data-controller="form-submit"
 *        data-form-submit-success-title-value="Thank You!"
 *        data-form-submit-success-message-value="We'll be in touch soon."
 *        data-form-submit-error-title-value="Oops!"
 *        data-form-submit-error-message-value="Something went wrong.">
 *
 *     <form data-form-submit-target="form"
 *           data-action="submit->form-submit#handleSubmit"
 *           action="/submit-endpoint" method="post">
 *
 *       <!-- Your form fields here -->
 *       <input type="text" name="email" required>
 *
 *       <button type="submit" data-form-submit-target="submitBtn">
 *         <span data-form-submit-target="btnText">Submit</span>
 *         <span data-form-submit-target="btnLoader" class="is-hidden">Loading...</span>
 *       </button>
 *     </form>
 *
 *     <div data-form-submit-target="feedback" class="is-hidden">
 *       <h3 data-form-submit-target="feedbackTitle"></h3>
 *       <p data-form-submit-target="feedbackMessage"></p>
 *     </div>
 *   </div>
 *
 * TARGETS:
 *   - form: The <form> element to submit
 *   - submitBtn: The submit button (gets disabled during loading)
 *   - btnText: Text inside button (hidden during loading)
 *   - btnLoader: Loader/spinner inside button (shown during loading)
 *   - feedback: Container for success/error message (shown after submission)
 *   - feedbackTitle: Element to display the title (e.g., "Thank You!")
 *   - feedbackMessage: Element to display the message
 *
 * VALUES (all optional):
 *   - successTitle: Title shown on successful submission
 *   - successMessage: Message shown on successful submission
 *   - errorTitle: Title shown on failed submission
 *   - errorMessage: Message shown on failed submission
 *   - hiddenClass: CSS class for hidden elements (default: "is-hidden")
 *   - fadeOutClass: CSS class for fade out animation (default: "fade-out")
 *   - fadeInClass: CSS class for fade in animation (default: "fade-in")
 *   - fadeDelay: Delay in ms before showing feedback (default: 400)
 *
 * EVENTS DISPATCHED:
 *   - form-submit:before: Fired before submission. Call event.preventDefault()
 *     in a listener to cancel the submission (useful for validation).
 *   - form-submit:complete: Fired after submission completes (success or error)
 */
export default class extends Controller {
  static targets = ["form", "feedback", "feedbackTitle", "feedbackMessage", "submitBtn", "btnText", "btnLoader"]
  static values = {
    successTitle: String,
    successMessage: String,
    errorTitle: String,
    errorMessage: String,
    hiddenClass: String,
    fadeOutClass: String,
    fadeInClass: String,
    fadeDelay: Number
  }

  get hiddenClass() {
    return this.hasHiddenClassValue ? this.hiddenClassValue : "is-hidden"
  }

  get fadeOutClass() {
    return this.hasFadeOutClassValue ? this.fadeOutClassValue : "fade-out"
  }

  get fadeInClass() {
    return this.hasFadeInClassValue ? this.fadeInClassValue : "fade-in"
  }

  get fadeDelay() {
    return this.hasFadeDelayValue ? this.fadeDelayValue : 400
  }

  // Handles form submission via AJAX.
  // Triggered by: data-action="submit->form-submit#handleSubmit" on the form
  handleSubmit(event) {
    event.preventDefault()

    const form = this.formTarget

    // Dispatch a cancellable "before" event - other controllers can prevent submission
    // Dispatch on the form so child controllers (like radio-toggle) can listen
    // Listeners can access the form via event.target or event.currentTarget
    const beforeEvent = this.dispatchFormEvent("form-submit:before", {}, { cancelable: true })

    // If another controller called event.preventDefault(), stop here
    if (beforeEvent.defaultPrevented) {
      return
    }

    const formData = new FormData(form)

    this.showLoading()

    fetch(form.action, {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        this.showFeedback(this.successTitleValue, this.successMessageValue)
        form.reset()
      } else {
        this.hideLoading()
        // Use backend error message if provided, otherwise fall back to generic
        const errorMessage = data.message || this.errorMessageValue
        this.showFeedback(this.errorTitleValue, errorMessage)
      }
    })
    .catch(error => {
      console.error('Error:', error)
      this.hideLoading()
      this.showFeedback(this.errorTitleValue, this.errorMessageValue)
    })
    .finally(() => {
      // Always executes, regardless of success or error
      this.dispatchFormEvent("form-submit:complete")
    })
  }

  // Shows loading state: disables button, hides text, shows spinner.
  showLoading() {
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = true
    }
    if (this.hasBtnTextTarget) {
      this.btnTextTarget.classList.add(this.hiddenClass)
    }
    if (this.hasBtnLoaderTarget) {
      this.btnLoaderTarget.classList.remove(this.hiddenClass)
    }
  }

  // Hides loading state: re-enables button, shows text, hides spinner.
  hideLoading() {
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = false
    }
    if (this.hasBtnTextTarget) {
      this.btnTextTarget.classList.remove(this.hiddenClass)
    }
    if (this.hasBtnLoaderTarget) {
      this.btnLoaderTarget.classList.add(this.hiddenClass)
    }
  }

  // Dispatches a custom event on the form element
  // Returns the dispatched event, so listeners can check if it was canceled (event.defaultPrevented)
  dispatchFormEvent(eventName, detail = {}, options = {}) {
    const event = new CustomEvent(eventName, {
      bubbles: true,
      cancelable: options.cancelable || false,
      detail: detail
    })

    this.formTarget.dispatchEvent(event)
    return event
  }

  // Transitions from form to feedback panel with fade animation.
  showFeedback(title, message) {
    if (this.hasFeedbackTitleTarget) {
      this.feedbackTitleTarget.textContent = title
    }
    if (this.hasFeedbackMessageTarget) {
      this.feedbackMessageTarget.textContent = message
    }

    // Start fade out animation on form
    this.formTarget.classList.add(this.fadeOutClass)

    // After animation delay, hide form and show feedback
    setTimeout(() => {
      this.formTarget.classList.add(this.hiddenClass)
      if (this.hasFeedbackTarget) {
        this.feedbackTarget.classList.remove(this.hiddenClass)
        this.feedbackTarget.classList.add(this.fadeInClass)
      }
    }, this.fadeDelay)
  }
}
